import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Search, Filter, Download, Trash2, Eye, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, BarChart3, LogOut, RefreshCw, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  in_corso: { label: 'In corso', color: 'bg-yellow-100 text-yellow-800' },
  inviato: { label: 'Inviato', color: 'bg-blue-100 text-blue-800' },
  nuovo: { label: 'Nuovo', color: 'bg-green-100 text-green-800' },
  contattato: { label: 'Contattato', color: 'bg-purple-100 text-purple-800' },
  convertito: { label: 'Convertito', color: 'bg-emerald-100 text-emerald-800' },
  abbandonato: { label: 'Abbandonato', color: 'bg-red-100 text-red-800' }
};

const routeLabels = {
  direct: { label: 'Diretto', color: 'bg-blue-50 text-blue-700' },
  dealer: { label: 'Rivenditore fisico', color: 'bg-orange-50 text-orange-700' },
  online: { label: 'Online', color: 'bg-purple-50 text-purple-700' }
};

const routingTypeLabels = {
  online_shop: 'Online Shop',
  physical_dealer: 'Rivenditore fisico',
  online_fallback: 'Online (fallback)'
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    provincia: '',
    search: ''
  });
  const [provinces, setProvinces] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [limit, setLimit] = useState(20);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.provincia) params.append('provincia', filters.provincia);
      if (filters.search) params.append('search', filters.search);

      const [leadsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/leads?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/leads/count?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setLeads(leadsRes.data);
      setTotalCount(countRes.data.count);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate('/admin6y5dv2x/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, filters, logout, navigate]);

  const fetchProvinces = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/provinces`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProvinces(res.data);
    } catch {
      // Province list is optional — filter dropdown simply stays empty.
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchProvinces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLeads]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.provincia) params.append('provincia', filters.provincia);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API_URL}/api/admin/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Esportazione fallita');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Eliminare definitivamente il lead?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchLeads();
    } catch (err) { alert('Eliminazione fallita'); }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/leads/${leadId}/status`, 
        { lead_id: leadId, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLeads();
    } catch { alert('Aggiornamento stato fallito'); }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-stocker-green rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Gestione Lead</h1>
              <p className="text-xs text-gray-500">{totalCount} lead totali</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin6y5dv2x/dashboard')}
            >
              Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin6y5dv2x/dealers')}
            >
              Rivenditori
            </Button>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate('/admin6y5dv2x/login'); }}
              className="text-gray-600 hover:text-red-600"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca (Nome, Email, Lead-ID)..."
                  value={filters.search}
                  onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
                  className="pl-9"
                  data-testid="leads-search-input"
                />
              </div>
            </div>

            <Select
              value={filters.status}
              onValueChange={(v) => { setFilters(f => ({ ...f, status: v === 'all' ? '' : v })); setPage(0); }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="in_corso">In corso</SelectItem>
                <SelectItem value="nuovo">Nuovo</SelectItem>
                <SelectItem value="contattato">Contattato</SelectItem>
                <SelectItem value="convertito">Convertito</SelectItem>
                <SelectItem value="inviato">Inviato</SelectItem>
                <SelectItem value="abbandonato">Abbandonato</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.provincia}
              onValueChange={(v) => setFilters(f => ({ ...f, provincia: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Provincia" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all">Tutte le province</SelectItem>
                {provinces.map(p => (
                  <SelectItem key={p.provincia} value={p.provincia}>
                    {p.provincia} ({p.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={() => { setFilters({ status: '', provincia: '', search: '' }); setPage(0); }}>
              <X className="w-4 h-4 mr-1" /> Reset
            </Button>

            <Button variant="outline" onClick={fetchLeads}>
              <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
            </Button>

            <Button onClick={handleExport} disabled={exporting} className="bg-stocker-green hover:bg-stocker-green-dark text-white">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Esporta Excel
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-stocker-green mx-auto" />
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nessun lead trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Provincia</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">m&sup2;</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kit</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Route</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rivenditore</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Data</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((lead) => (
                    <tr key={lead.lead_id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">{[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{lead.email || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{lead.provincia || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{lead.area_m2 || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-[120px] truncate">{lead.kit_selected_name || '-'}</td>
                      <td className="px-3 py-3">
                        {lead.route ? (
                          <div>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${routeLabels[lead.route]?.color || 'bg-gray-100'}`}>
                              {routeLabels[lead.route]?.label || lead.route}
                            </span>
                            {lead.routing_type && (
                              <p className="text-[10px] text-gray-400 mt-0.5">{routingTypeLabels[lead.routing_type] || lead.routing_type}</p>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-[120px] truncate">{lead.assigned_dealer_name || '-'}</td>
                      <td className="px-3 py-3">
                        <select
                          data-testid={`status-select-${lead.lead_id}`}
                          value={lead.status}
                          onChange={(e) => handleStatusChange(lead.lead_id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${statusLabels[lead.status]?.color || 'bg-gray-100'}`}
                        >
                          <option value="in_corso">In corso</option>
                          <option value="nuovo">Nuovo</option>
                          <option value="contattato">Contattato</option>
                          <option value="convertito">Convertito</option>
                          <option value="inviato">Inviato</option>
                          <option value="abbandonato">Abbandonato</option>
                        </select>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin6y5dv2x/leads/${lead.lead_id}`)} className="h-8 w-8 p-0">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(lead.lead_id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="border-t border-gray-200 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600">
                  Mostra {totalCount === 0 ? 0 : page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} di {totalCount}
                </p>
                <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v, 10)); setPage(0); }}>
                  <SelectTrigger className="w-[110px] h-8 text-xs" data-testid="leads-perpage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 / pagina</SelectItem>
                    <SelectItem value="50">50 / pagina</SelectItem>
                    <SelectItem value="100">100 / pagina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button data-testid="leads-prev-page-btn" variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Pagina {page + 1} di {totalPages}</span>
                  <Button data-testid="leads-next-page-btn" variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminLeads;
